import type { SelectOption } from '@/components/ui/form-fields'

import { ConstructionType, StockItemStatus, StockItemType } from '../types'
import type {
  AdjustmentKind,
  AdjustmentStatus,
  CostMethod,
  CountLineWarning,
  CountScope,
  CountStatus,
  ReasonCategory,
  InventoryLocation,
  MovementSourceType,
  MovementType,
  InventoryUnit,
  InventoryUnitFamily,
  Recipe,
  RecipeModifierMode,
  RecipeStatus,
  StockItem,
  TrackingType,
  UnitOfMeasure,
} from '../types'

/**
 * Everything the inventory screens turn into words.
 *
 * In one file for the reason the customer module keeps its own: the same
 * vocabulary reaches the table, the filters and — later — the item drawer, and
 * the fastest way for "Packaging" to become "Packing" one panel over is to write
 * it where it is used.
 */

export const itemTypeLabels: Record<StockItemType, string> = {
  [StockItemType.Product]: 'Product',
  [StockItemType.Ingredient]: 'Ingredient',
  [StockItemType.Packaging]: 'Packaging',
  [StockItemType.Material]: 'Material',
}

export const itemStatusLabels: Record<StockItemStatus, string> = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  ARCHIVED: 'Archived',
}

export const trackingLabels: Record<TrackingType, string> = {
  STOCKED: 'Counted',
  NON_STOCKED: 'Not counted',
}

export const constructionLabels: Record<ConstructionType, string> = {
  [ConstructionType.Simple]: 'Bought or received as is',
  [ConstructionType.Composite]: 'Made from other items',
}

export const costMethodLabels: Record<CostMethod, string> = {
  AVCO: 'Average cost',
  FIFO: 'First in, first out',
}

export const recipeStatusLabels: Record<RecipeStatus, string> = {
  DRAFT: 'Draft',
  ACTIVE: 'Active',
  INACTIVE: 'Archived',
}

/** A unit family as `/units` names it. */
export const inventoryUnitFamilyLabels: Record<InventoryUnitFamily, string> = {
  weight: 'Weight',
  volume: 'Volume',
  count: 'Count',
  own: 'Your own',
}

export const modifierModeLabels: Record<RecipeModifierMode, string> = {
  ADD: 'Adds an ingredient',
  REMOVE: 'Removes an ingredient',
  REPLACE: 'Swaps an ingredient',
}

export const locationTypeLabels: Record<InventoryLocation['type'], string> = {
  branch: 'Branch',
  warehouse: 'Warehouse',
}

export const locationStatusLabels: Record<InventoryLocation['status'], string> = {
  active: 'Active',
  inactive: 'Inactive',
}

export const adjustmentKindLabels: Record<AdjustmentKind, string> = {
  adjustment: 'Adjustment',
  wastage: 'Wastage',
  opening: 'Opening balance',
}

/** What each kind is for, under the kind picker. */
export const adjustmentKindHints: Record<AdjustmentKind, string> = {
  adjustment: 'Add or remove a known amount — found a case, stolen, a typing mistake.',
  wastage: 'Stock that left without a sale — expired, spoiled, staff meals.',
  opening: 'The starting quantity and cost, for an item with no stock history here yet.',
}

export const documentStatusLabels: Record<AdjustmentStatus | CountStatus, string> = {
  draft: 'Draft',
  counting: 'Counting',
  pending_approval: 'Waiting for approval',
  posted: 'Posted',
  cancelled: 'Cancelled',
}

export const documentStatusColors: Record<
  AdjustmentStatus | CountStatus,
  'default' | 'warning' | 'success' | 'danger'
> = {
  draft: 'default',
  counting: 'default',
  pending_approval: 'warning',
  posted: 'success',
  cancelled: 'danger',
}

export const reasonCategoryLabels: Record<ReasonCategory, string> = {
  waste: 'Waste',
  internal_use: 'Internal use',
  comp: 'Comp',
  shrinkage: 'Shrinkage',
  correction: 'Correction',
  opening: 'Opening',
}

/** The reasons a line of each kind may pick from; an opening takes "Opening balance" itself. */
export const reasonCategoriesOf: Record<AdjustmentKind, ReasonCategory[]> = {
  adjustment: ['shrinkage', 'correction'],
  wastage: ['waste', 'internal_use', 'comp'],
  opening: [],
}

export const countScopeLabels: Record<CountScope, string> = {
  full: 'Every counted item',
  partial: 'Items I pick',
}

export const countWarningLabels: Record<CountLineWarning, string> = {
  EXPECTED_ZERO: 'Nothing was expected here — check the count.',
  LARGE_VARIANCE: 'Far from what was expected — check the count.',
}

export const movementTypeLabels: Record<MovementType, string> = {
  RECEIPT: 'Receiving',
  CONSUMPTION: 'Sale deduction',
  RETURN: 'Return',
  WASTE: 'Waste',
  WASTE_RETURN: 'Waste return',
  ADJUSTMENT: 'Adjustment',
  TRANSFER_OUT: 'Transfer out',
  TRANSFER_IN: 'Transfer in',
}

export const movementSourceLabels: Record<MovementSourceType, string> = {
  ORDER_FIRE: 'Kitchen fire',
  ORDER_VOID: 'Void',
  ORDER_WASTE: 'POS waste',
  ORDER_COMP: 'Comp',
  SHIPMENT: 'Shipment',
  REFUND: 'Refund',
  PURCHASE_ORDER: 'Purchase order',
  STOCK_COUNT: 'Stock count',
  COUNT_ANCHOR: 'Late movement behind a count',
  TRANSFER: 'Transfer',
  ADJUSTMENT: 'Adjustment',
  PRODUCTION_RUN: 'Production run',
  SYSTEM: 'System',
  ORDER: 'Order',
  POS_TRANSACTION: 'POS sale',
  WASTAGE: 'Wastage',
}

/** The active units by code, as `useUnitCatalog` serves them. */
type Units = ReadonlyMap<string, InventoryUnit>

/**
 * The unit a code converts through — kg and g both go through g — which is the
 * rule the service enforces: a recipe line may use any unit that shares its
 * ingredient's base, and no other. An unknown code is its own base.
 */
function baseOf(code: UnitOfMeasure, units: Units): string {
  const unit = units.get(code)
  return unit ? (unit.baseCode ?? unit.code) : code
}

/** How many of its base one of the unit is: 1000 for kg, 1 for g itself. */
function factorOf(code: UnitOfMeasure, units: Units): number {
  return units.get(code)?.factorToBase ?? 1
}

/**
 * `qty` of `from`, in `to` — 250 g is 0.25 kg. `null` between units with no
 * common base: grams of something counted in each, or ounces of something
 * weighed in grams, have no answer the service would accept either.
 */
export function convertQty(
  qty: number,
  from: UnitOfMeasure,
  to: UnitOfMeasure,
  units: Units,
): number | null {
  if (from === to) return qty
  if (!units.has(from) || !units.has(to) || baseOf(from, units) !== baseOf(to, units)) return null
  return (qty * factorOf(from, units)) / factorOf(to, units)
}

/**
 * What one unit of the item costs — the service's own rule: the average cost
 * once a receipt has set one, the default cost until then. `0` when neither is.
 */
export function itemUnitCost(item: StockItem): number {
  return item.currentAvgCost || item.defaultUnitCost
}

/** What one recipe line costs, and the unit cost it was worked out from. */
export interface LineCost {
  cost: number
  /** The ingredient's cost per one of its own unit. `0` when it has none yet. */
  perUnit: number
  unit: UnitOfMeasure
}

/**
 * What `qty` of `unit` of the ingredient costs — converted first, since a line
 * can be in grams of something costed per kilo. `null` while the ingredient or
 * the quantity is unknown, or the unit measures something else.
 */
export function lineCostOf(
  qty: number | undefined,
  unit: UnitOfMeasure,
  ingredient: StockItem | undefined,
  units: Units,
): LineCost | null {
  if (!ingredient || qty === undefined || !Number.isFinite(qty)) return null

  const inItemUnit = convertQty(qty, unit, ingredient.unitOfMeasure, units)
  if (inItemUnit === null) return null

  const perUnit = itemUnitCost(ingredient)
  return { cost: inItemUnit * perUnit, perUnit, unit: ingredient.unitOfMeasure }
}

/** A saved recipe's cost, as a list row shows it. */
export interface RecipeCost {
  total: number
  /** Lines whose ingredient has no cost yet — the total leaves them out. */
  uncosted: number
  /** Lines whose ingredient has not loaded — the total is not ready while any are. */
  pending: number
}

/**
 * What a saved recipe costs to make once, from its ingredients' unit costs.
 *
 * A line in another unit than its ingredient's waits for the units list, which
 * says how to convert it; until then it counts as pending, not as free.
 */
export function recipeCostOf(
  recipe: Recipe,
  ingredients: ReadonlyMap<string, StockItem>,
  units: Units,
): RecipeCost {
  let total = 0
  let uncosted = 0
  let pending = 0

  for (const line of recipe.lines) {
    const ingredient = ingredients.get(line.itemId)
    const awaitsUnits = units.size === 0 && line.unitOfMeasure !== ingredient?.unitOfMeasure
    if (!ingredient || awaitsUnits) {
      pending += 1
      continue
    }

    const cost = lineCostOf(line.qty, line.unitOfMeasure, ingredient, units)
    if (!cost || cost.perUnit === 0) uncosted += 1
    else total += cost.cost
  }

  return { total, uncosted, pending }
}

/**
 * The units a line may use for an ingredient measured in `unit`: itself first,
 * then every active unit sharing its base, smallest first — g, then kg. Just
 * the unit itself while the list loads, or for a business's own unit.
 */
export function unitsLike(unit: UnitOfMeasure, units: Units): UnitOfMeasure[] {
  const base = baseOf(unit, units)
  const others = [...units.values()]
    .filter((candidate) => candidate.code !== unit && baseOf(candidate.code, units) === base)
    .sort((a, b) => (a.factorToBase ?? 1) - (b.factorToBase ?? 1))
    .map((candidate) => candidate.code)

  return [unit, ...others]
}

/** The order families are shown in: the three that convert, then a business's own. */
const FAMILY_ORDER: InventoryUnitFamily[] = ['weight', 'volume', 'count', 'own']

/** One family's units, as the Units tab and the base-unit select list them. */
export interface UnitGroup {
  family: InventoryUnitFamily
  label: string
  units: InventoryUnit[]
}

/**
 * Units grouped by what they measure — Weight, Volume, Count, Your own — with
 * no group for a family that has none. Inside a group, each base comes before
 * the units built on it, smallest first: g, kg, then oz, lb.
 */
export function unitGroups(units: Iterable<InventoryUnit>): UnitGroup[] {
  const all = [...units]
  const baseCode = (unit: InventoryUnit) => unit.baseCode ?? unit.code

  return FAMILY_ORDER.map((family) => ({
    family,
    label: inventoryUnitFamilyLabels[family],
    units: all
      .filter((unit) => unit.family === family)
      .sort(
        (a, b) =>
          baseCode(a).localeCompare(baseCode(b)) ||
          (a.factorToBase ?? 1) - (b.factorToBase ?? 1) ||
          a.code.localeCompare(b.code),
      ),
  })).filter((group) => group.units.length > 0)
}

/**
 * How a unit converts: `1 kg = 1000 g` for one built on a base, "Base unit"
 * for a base, and a dash for a business's own unit, which stands alone.
 */
export function unitConversion(unit: InventoryUnit): string {
  if (unit.factorToBase !== null && unit.baseCode) {
    return `1 ${unit.code} = ${recipeQuantity(unit.factorToBase)} ${unit.baseCode}`
  }
  return unit.family === 'own' ? '—' : 'Base unit'
}

/** Every active unit for a base-unit select, under its family's heading. */
export function unitOptionGroups(units: Units): { label: string; options: SelectOption[] }[] {
  return unitGroups(units.values()).map((group) => ({
    label: group.label,
    options: group.units.map((unit) => ({ value: unit.code, label: unit.code })),
  }))
}

/**
 * Whether a row opens its detail: an item made from a recipe, or an ingredient
 * that goes into one. Everything else is fully described by its row.
 */
export function hasDetail(item: StockItem): boolean {
  return (
    item.constructionType === ConstructionType.Composite ||
    item.stockItemType === StockItemType.Ingredient
  )
}

/**
 * Whether the item takes a recipe: a product made from other items, and not
 * archived — the API refuses a recipe for an archived item, so the button is not
 * offered.
 */
export function canHaveRecipe(item: StockItem): boolean {
  return (
    item.constructionType === ConstructionType.Composite &&
    item.stockItemType === StockItemType.Product &&
    item.status !== StockItemStatus.Archived
  )
}

const costFormat = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 6,
})

/**
 * `$1.25`, `$0.000125`.
 *
 * Up to six decimals — what the API stores — unlike takings: this is a cost per
 * unit, and a gram of saffron costs a fraction of a cent that two decimals would
 * round to nothing.
 */
export function unitCost(value: number): string {
  return `$${costFormat.format(value)}`
}

/** A unit cost as a number field takes it: the same six decimals, in dollars. */
export const UNIT_COST_FORMAT: Intl.NumberFormatOptions = {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 6,
}

const valueFormat = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/**
 * `$1,250.00`, `−$12.50`.
 *
 * What the stock is worth at cost — on hand × average cost — so cents, not the
 * four decimals of a unit cost: the fractions of a cent add up into the total.
 * A shortfall is worth a negative amount, with the same true minus as `quantity`.
 */
export function stockValue(value: number): string {
  const sign = value < 0 ? '−' : ''

  return `${sign}$${valueFormat.format(Math.abs(value))}`
}

const recipeCostFormat = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
})

/**
 * `$0.36`, `$0.0125`.
 *
 * What an ingredient line, or a whole recipe, costs to make. Up to four
 * decimals: a pinch of salt costs a fraction of a cent, and two decimals would
 * show it as free.
 */
export function recipeCost(value: number): string {
  return `$${recipeCostFormat.format(value)}`
}

const quantityFormat = new Intl.NumberFormat(undefined, { maximumFractionDigits: 3 })
const recipeQuantityFormat = new Intl.NumberFormat(undefined, { maximumFractionDigits: 4 })

/**
 * `0.0005`. A recipe line takes the fourth decimal a stock figure does not — a
 * pinch of salt is half a gram of a kilo — so it is printed with it.
 */
export function recipeQuantity(value: number): string {
  return recipeQuantityFormat.format(value)
}

/**
 * `12`, `0.25`, `−3`.
 *
 * Up to three decimals, because stock is weighed as well as counted — 0.25 kg of
 * matcha is a real quantity — and no more, because a fourth is noise from the
 * cost arithmetic rather than anything on a shelf. A true minus, so a shortfall
 * lines up with the numbers above it.
 */
export function quantity(value: number): string {
  const sign = value < 0 ? '−' : ''

  return `${sign}${quantityFormat.format(Math.abs(value))}`
}

/**
 * `+3`, `−0.25`, `0` — what an adjustment line changes on hand by. Always signed,
 * so a gain and a loss read apart at a glance, and to the four decimals an
 * adjustment takes.
 */
export function variance(value: number): string {
  const sign = value > 0 ? '+' : value < 0 ? '−' : ''

  return `${sign}${recipeQuantityFormat.format(Math.abs(value))}`
}

const dateTimeFormat = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
})

/** `20 Oct 2026, 14:05` — when an adjustment was saved, or stock moved. */
export function dateTime(iso: string): string {
  return dateTimeFormat.format(new Date(iso))
}

/**
 * Whether the item has quantities at all.
 *
 * A `NON_STOCKED` item is listed but never counted, so its zeros are not "none
 * left" — they are "not tracked", and the table says that instead of showing a
 * row of zeros that reads as sold out.
 */
export function isCounted(item: StockItem): boolean {
  return item.trackingType === 'STOCKED' || item.constructionType === ConstructionType.Composite
}

/** Where a row's stock stands, as its Status column says it. */
export type StockStatus = 'in' | 'low' | 'recipe' | 'out' | 'untracked' | 'recipe'

export const stockStatusLabels: Record<StockStatus, string> = {
  in: 'In stock',
  low: 'Low stock',
  recipe: 'Recipe-based',
  out: 'Out of stock',
  untracked: 'Untracked',
}

/**
 * Untracked for an item that is never counted, and recipe-based for one made
 * from other items — its stock is its ingredients'. Otherwise out of stock at
 * zero or below, low at or below the item's reorder point, and in stock above it.
 *
 * Judged on what is available — on hand less what is reserved — since reserved
 * stock cannot be sold or used.
 *
 * "Low" is only judged on the totals. The reorder point is set for the item as a
 * whole, so measuring one location's share against it would call a well-stocked
 * business low because one outlet holds a fifth of it. "Out" holds anywhere:
 * nothing on the shelf is nothing on the shelf.
 *
 * An archived item is never low — nobody is reordering it.
 */
export function stockStatus(
  item: StockItem,
  available: number,
  atOneLocation: boolean,
): StockStatus {    
  if(item.constructionType == ConstructionType.Composite && item.stockItemType == StockItemType.Product) return 'recipe'
  if (!isCounted(item)) return 'untracked'
  if (available <= 0) return 'out'
  if (!atOneLocation && item.status !== 'ARCHIVED' && available <= item.reorderPoint) return 'low'

  return 'in'
}

/** `Phnom Penh central`, and `Riverside store (warehouse)` for a warehouse. */
export function locationLabel(location: InventoryLocation): string {
  return location.type === 'warehouse' ? `${location.name} (warehouse)` : location.name
}

/**
 * Locations as the picker lists them: each branch followed by the warehouses it
 * owns, then the central warehouses that belong to no branch.
 *
 * The API sorts by type, then name — every branch, then every warehouse — which
 * separates a warehouse from the outlet it stocks. Grouping them back keeps the
 * question "where is this?" answered by where it sits in the list. A warehouse
 * whose branch is not in the list (closed, or out of scope) goes with the
 * central ones rather than vanishing.
 */
export function inOutletOrder(locations: readonly InventoryLocation[]): InventoryLocation[] {
  const branches = locations.filter((location) => location.type === 'branch')
  const outlets = new Set(branches.map((branch) => branch.branchId))
  const warehouses = locations.filter((location) => location.type === 'warehouse')

  return [
    ...branches.flatMap((branch) => [
      branch,
      ...warehouses.filter((warehouse) => warehouse.branchId === branch.branchId),
    ]),
    ...warehouses.filter((warehouse) => !warehouse.branchId || !outlets.has(warehouse.branchId)),
  ]
}
